import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClientFullData } from '@/types/client';

interface ConfigTabProps {
  clientId: string;
  client: ClientFullData;
}

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-surface-1 border border-border rounded overflow-hidden">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex items-center gap-2 w-full px-4 py-3 text-left hover:bg-surface-2 transition-colors"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="font-heading text-sm uppercase tracking-wider text-foreground">
          {title}
        </span>
      </button>
      {isOpen && <div className="px-4 pb-4 border-t border-border">{children}</div>}
    </div>
  );
};

export const ConfigTab: React.FC<ConfigTabProps> = ({ clientId, client }) => {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground font-label uppercase tracking-wider mb-2">
        Client configuration — expand sections to view settings
      </p>

      <CollapsibleSection title="Basic Info" defaultOpen>
        <div className="space-y-2 pt-3 text-sm">
          <ConfigRow label="Name" value={client.name} />
          <ConfigRow label="Domain" value={client.domain} />
          <ConfigRow label="Physical Address" value={client.physical_address} />
          <ConfigRow label="Time Zone" value={client.time_zone} />
          <ConfigRow label="Description" value={client.description} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Billing & Invoicing">
        <div className="space-y-2 pt-3 text-sm">
          <ConfigRow label="Employment Type" value={client.employment_type} />
          <ConfigRow label="Payment Terms" value={client.payment_terms_days != null ? `${client.payment_terms_days} days` : null} />
          <ConfigRow label="AP Email" value={client.accounts_payable_email} />
          <ConfigRow label="Billing Address" value={client.billing_address} />
          <ConfigRow label="Half Day Hours" value={client.half_day_hours != null ? `${client.half_day_hours}h` : null} />
          <ConfigRow label="Min Billable Hours" value={client.minimum_billable_hours != null ? `${client.minimum_billable_hours}h` : null} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Surcharges & Premiums">
        <div className="space-y-2 pt-3 text-sm">
          <ConfigRow label="Meal Surcharge" value={client.apply_meal_surcharge ? 'Enabled' : 'Disabled'} />
          <ConfigRow label="Short Turn" value={client.uses_short_turn ? 'Enabled' : 'Disabled'} />
          <ConfigRow label="Holiday Premium" value={client.apply_holiday_premium ? 'Enabled' : 'Disabled'} />
          <ConfigRow label="Overnight Premium" value={client.apply_overnight_premium ? 'Enabled' : 'Disabled'} />
          <ConfigRow label="Premium Shifts" value={client.allow_premium_shifts ? 'Enabled' : 'Disabled'} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Travel & Expenses">
        <div className="space-y-2 pt-3 text-sm">
          <ConfigRow label="Per Diem" value={client.apply_per_diem ? 'Enabled' : 'Disabled'} />
          <ConfigRow
            label="Per Diem Rate"
            value={client.per_diem_base_rate != null ? `$${client.per_diem_base_rate}` : null}
          />
          <ConfigRow
            label="Travel Day Rate Mult."
            value={client.travel_day_rate_multiplier != null ? `${client.travel_day_rate_multiplier}x` : null}
          />
          <ConfigRow
            label="Distance Threshold"
            value={client.travel_distance_threshold != null ? `${client.travel_distance_threshold} mi` : null}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Gear & Kit Fees">
        <div className="space-y-2 pt-3 text-sm">
          {client.gear_items ? (
            <p className="text-muted-foreground">
              {(client.gear_items as unknown[]).length} gear item(s) configured
            </p>
          ) : (
            <p className="text-muted-foreground">No gear items configured.</p>
          )}
          {client.gear_kits ? (
            <p className="text-muted-foreground">
              {(client.gear_kits as unknown[]).length} kit(s) configured
            </p>
          ) : (
            <p className="text-muted-foreground">No kits configured.</p>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

function ConfigRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground font-label text-xs uppercase tracking-wider flex-shrink-0">
        {label}
      </span>
      <span className="text-foreground text-right font-mono min-w-0 break-words max-w-[60%]">
        {value ?? <span className="text-muted-foreground italic font-sans">Not set</span>}
      </span>
    </div>
  );
}
